import {Injectable, OnDestroy} from '@angular/core';
import {GeneralService} from './general.service';
import {AuthService} from './auth.service';
import {catchError, EMPTY, expand, forkJoin, Observable, of, reduce, Subject, switchMap, tap} from 'rxjs';
import {map} from 'rxjs/operators';
import {NICEStudioScript} from '../models/NICE_StudioScript.model';
import {UtilityService} from './utility.service';
import {Router} from '@angular/router';
import {DateTime} from 'luxon';
import {NICEFile} from "../models/NICE_File.model";


@Injectable({
    providedIn: 'root'
})
export class IcDataStudioScriptsService implements OnDestroy{
    private studioScriptsMap: Map<string, NICEStudioScript> = new Map();
    private scriptsLastUpdatedDate: DateTime = DateTime.fromISO('1900-01-01');
    private _unsubscribeAll: Subject<any> = new Subject<any>();


    constructor(public genHttp: GeneralService,
                public auth: AuthService,
                public utility: UtilityService,
                private router: Router,
    ) {
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.studioScriptsMap.clear();
        this.scriptsLastUpdatedDate = null;
    }

    public initializeData(){
        this.studioScriptsMap.clear();
        this.scriptsLastUpdatedDate = DateTime.fromISO('1900-01-01');
        return this.getStudioScripts();
    }

    public getStudioScripts(): Observable<NICEStudioScript[]>{
        const stopDate = DateTime.utc();
        let includeInactive = 'false';
        if (this.studioScriptsMap.size > 0)
        {
            includeInactive = 'true';
        }
        const URL = 'baseURL/services/' + this.auth.apiVersion +
            '/scripts/search?includeInactive='+includeInactive
            +'&scriptName=&skip=0&orderBy=masterID'
            + '&modStartDate=' + encodeURIComponent(this.scriptsLastUpdatedDate.toISO())
            + '&modEndDate=' + encodeURIComponent(stopDate.toISO())
            + '&top=100'
            + '&fields=busNo,masterID,scriptName,status,mediaType,modifyDate,mUser';
        this.scriptsLastUpdatedDate = stopDate;
        const $updatedScriptList: Observable<NICEStudioScript[]> = this.genHttp.icGet(URL
        ).pipe(
            expand((response) =>
            {if (response._links.next)
            {
                const nextURL = 'baseURL' + response._links.next.substring(response._links.next.indexOf('/services/'));
                return this.genHttp.icGet(nextURL);
            }
                return EMPTY;
            }),
            reduce((acc, current) => acc.concat(current.scriptSearchDetails.map(data => new NICEStudioScript(data))), []),
            map((updateArray) => {
                updateArray.forEach(item => this.studioScriptsMap.set(item.scriptPath, {...this.studioScriptsMap.get(item.scriptPath), ...item}));
                updateArray.forEach((item) => {
                    if(item.scriptStatus !== 'CURR'){
                        this.studioScriptsMap.delete(item.scriptPath);
                    }
                });
                return Array.from(this.studioScriptsMap.values())
                    .sort((a, b) => a.scriptName.localeCompare(b.scriptName)) as NICEStudioScript[];
            }),
            catchError((err) => {
                // Handle specific error for this call
                return of(
                Array.from(this.studioScriptsMap.values())
                    .sort((a, b) => a.scriptName.localeCompare(b.scriptName)) as NICEStudioScript[]
                );
            })
        );
        const path = '/AAC_Script_Audits';
        const listURL = 'baseURL/services/v29.0/scripts/files/search?rootFolder=' + encodeURIComponent(path);
        const $auditMarkers: Observable<NICEFile[]> = this.genHttp.icGet(listURL)
                .pipe(map((response: any) => response.files.map(data => new NICEFile(data))),
                    catchError((err) => {
                        // Handle specific error for this call
                        const emptyFiles: NICEFile[] = [];
                        return of(emptyFiles)}));
        return forkJoin([$updatedScriptList,$auditMarkers]).pipe(
            map(([updatedScripts,updateFileMarkers]) => {
                const markerMap = new Map();
            updateFileMarkers.forEach(item => markerMap.set(item.fileName.replace('.txt','').replace('.json','').replace(/--DIR--/g, '\\'), item));
                updatedScripts.forEach(item =>
                        item.lastExecuted = markerMap.get(item.scriptPath)?.modified);
                return updatedScripts;
            }
        ));
    }

    public getScriptSchedules(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('schedulebaseURL/services/v29.0/script-schedules')
                // .pipe(map((response: any) => new NICEStudioScript(response)))
                .subscribe((response) => {
                    resolve(response);
                }, reject);
        });
    }

    public getScriptSchedule(scriptId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/v29.0/script-schedules/' + scriptId)
                // .pipe(map((response: any) => new NICEStudioScript(response)))
                .subscribe((response) => {
                    resolve(response.scriptSchedule);
                }, reject);
        });
    }

    public getStudioScript(scriptId: string): Promise<NICEStudioScript> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/scripts/' + scriptId)
                .pipe(map((response: any) => new NICEStudioScript(response)))
                .subscribe((response: NICEStudioScript) => {
                    resolve(response);
                }, reject);
        });
    }

    public saveStudioScript(script: NICEStudioScript): Observable<any> {
        return this.genHttp.icPost('baseURL/services/v19.0/scripts', JSON.stringify(script));
    }

    public deleteStudioScript(scriptPath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icDelete('baseURL/services/v19.0/scripts?scriptPath=' + encodeURIComponent(scriptPath), '')
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    /**
     * Retrieves the history of a script based on its path.
     *
     * @param {string} scriptPath - The path of the script.
     * @return {Promise<any>} - A promise that resolves to the history of the script.
     */
    public getScriptHistory(scriptPath: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/v19.0/scripts/historyByName?scriptPath=' + encodeURIComponent(scriptPath))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }
    public findStudioScriptByPath(path:string){
        return this.studioScriptsMap.get(path);
    }

}
